import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Snackbar,
  Collapse,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  LinearProgress,
} from '@material-ui/core';
import {
  PlayArrow as StartIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  CheckCircle as PassIcon,
  Cancel as FailIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Assignment as AssignmentIcon,
  Build as BuildIcon,
  Input as InputIcon,
  Visibility as ViewIcon,
  Dashboard as SummaryIcon,
} from '@material-ui/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { RootState } from '../../store';
import {
  fetchWorkOrderByIdStart,
  fetchOperationLogsStart,
  createOperationLogStart,
  updateOperationLogStart,
  fetchSerialsStart,
  approveSerialStart,
  rejectSerialStart,
  clearSelectedWorkOrder,
  clearError,
} from '../../store/reducers/workOrderSlice';
import {
  workOrderApi,
  WorkOrder,
  WorkOrderSerial,
  WorkOrderOperationLog,
  WorkOrderOperation,
  OperationLogRequest,
  WorkOrderOperationInput,
  WorkInstruction,
  ToolFixture,
} from '../../services/workOrderApi';

// Custom Alert component since @material-ui/core v4 doesn't have Alert
const Alert: React.FC<{ severity: 'error' | 'warning' | 'info' | 'success'; children: React.ReactNode }> = ({ 
  severity, 
  children 
}) => (
  <Box 
    p={2} 
    style={{ 
      backgroundColor: severity === 'error' ? '#ffebee' : severity === 'success' ? '#e8f5e8' : '#e3f2fd',
      border: `1px solid ${severity === 'error' ? '#f44336' : severity === 'success' ? '#4caf50' : '#2196f3'}`,
      borderRadius: '4px',
      marginBottom: '1rem'
    }}
  >
    <Typography color={severity === 'error' ? 'error' : severity === 'success' ? 'primary' : 'textPrimary'}>
      {children}
    </Typography>
  </Box>
);

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`work-order-tabpanel-${index}`}
      aria-labelledby={`work-order-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  );
}

/**
 * WorkOrderEdit component for managing individual work orders and their manufacturing execution
 */
const WorkOrderEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();

  const {
    selectedWorkOrder,
    loading,
    error,
    activeOperationLogs,
    operationLogsLoading,
    selectedSerials,
    serialsLoading,
  } = useSelector((state: RootState) => state.workOrder);

  const { user } = useSelector((state: RootState) => state.auth);

  // Local state
  const [currentTab, setCurrentTab] = useState(0);
  const [expandedOperations, setExpandedOperations] = useState<string[]>([]);
  const [selectedOperation, setSelectedOperation] = useState<WorkOrderOperation | null>(null);
  const [selectedSerial, setSelectedSerial] = useState<string>('');
  const [operationStatus, setOperationStatus] = useState<'IN_PROGRESS' | 'PASSED' | 'FAILED' | 'PAUSED'>('IN_PROGRESS');
  const [showOperationDialog, setShowOperationDialog] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [workOrderData, setWorkOrderData] = useState<WorkOrder | null>(null);
  const [loadingData, setLoadingData] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  // Load work order data
  useEffect(() => {
    if (id) {
      loadWorkOrderData(id);
    }

    return () => {
      dispatch(clearSelectedWorkOrder());
    };
  }, [dispatch, id]);

  const loadWorkOrderData = async (workOrderId: string) => {
    setLoadingData(true);
    setApiError(null);
    try {
      const woData = await workOrderApi.getById(workOrderId);
      setWorkOrderData(woData);
      
      // Also dispatch Redux actions for backward compatibility
      dispatch(fetchWorkOrderByIdStart(workOrderId));
      dispatch(fetchOperationLogsStart(workOrderId));
      dispatch(fetchSerialsStart(workOrderId));
    } catch (error: any) {
      console.error('Error loading work order:', error);
      setApiError(error.message || 'Failed to load work order data. Please try again.');
    } finally {
      setLoadingData(false);
    }
  };

  // Auto-refresh operation logs every 5 seconds
  useEffect(() => {
    if (!id) return;

    const interval = setInterval(() => {
      dispatch(fetchOperationLogsStart(id));
    }, 5000);

    return () => clearInterval(interval);
  }, [dispatch, id]);

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleOperationExpand = (operationId: string) => {
    setExpandedOperations(prev => 
      prev.includes(operationId) 
        ? prev.filter(id => id !== operationId)
        : [...prev, operationId]
    );
  };

  const handleStartOperation = (operation: WorkOrderOperation, serialNumber: string) => {
    if (!selectedWorkOrder || !user) return;

    const logData: OperationLogRequest = {
      WOKey: selectedWorkOrder.id!,
      OperatorKey: user.id,
      RCTKey: selectedWorkOrder.route_card?.id || '',
      OperationKey: operation.id!,
      SerialNumber: serialNumber,
      Status: 'IN_PROGRESS',
    };

    dispatch(createOperationLogStart(logData));
    setSnackbarMessage(`Started operation: ${operation.Operation?.Name} for serial: ${serialNumber}`);
    setSnackbarOpen(true);
  };

  const handleCompleteOperation = (logId: string, status: 'PASSED' | 'FAILED') => {
    const logData = {
      id: logId,
      Status: status,
      EndTime: new Date().toISOString(),
    };

    dispatch(updateOperationLogStart(logData));
    setSnackbarMessage(`Operation marked as ${status.toLowerCase()}`);
    setSnackbarOpen(true);
  };

  const handleApproveSerial = (serialNum: string) => {
    if (!selectedWorkOrder) return;

    dispatch(approveSerialStart({ 
      workOrderId: selectedWorkOrder.id!, 
      serialNum 
    }));
    setSnackbarMessage(`Serial ${serialNum} approved`);
    setSnackbarOpen(true);
  };

  const handleRejectSerial = (serialNum: string, reason?: string) => {
    if (!selectedWorkOrder) return;

    dispatch(rejectSerialStart({ 
      workOrderId: selectedWorkOrder.id!, 
      serialNum, 
      reason 
    }));
    setSnackbarMessage(`Serial ${serialNum} rejected`);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    dispatch(clearError());
  };

  const getStatusColor = (status: string): 'primary' | 'secondary' | 'default' => {
    switch (status) {
      case 'IN_PROGRESS': return 'primary';
      case 'PASSED': return 'secondary';
      case 'FAILED': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'IN_PROGRESS': return <StartIcon />;
      case 'PASSED': return <PassIcon />;
      case 'FAILED': return <FailIcon />;
      case 'PAUSED': return <PauseIcon />;
      default: return <StopIcon />;
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const calculateDuration = (startTime: string, endTime?: string) => {
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const durationMs = end.getTime() - start.getTime();
    const durationMinutes = Math.floor(durationMs / (1000 * 60));
    return `${durationMinutes} min`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" style={{ marginLeft: '1rem' }}>
          Loading Work Order...
        </Typography>
      </Box>
    );
  }

  if (!selectedWorkOrder) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Work Order not found or failed to load.
        </Alert>
        <Button onClick={() => navigate('/workorders')} variant="contained">
          Back to Work Orders
        </Button>
      </Box>
    );
  }

  const currentWorkOrder = workOrderData || selectedWorkOrder;
  const currentRouteCard = currentWorkOrder?.route_card;
  const operations = currentRouteCard?.operations || [];

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1">
            Work Order: {currentWorkOrder?.wo_num || 'Loading...'}
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            Purchase Order: {currentWorkOrder?.purchase_order?.id || 'N/A'}
          </Typography>
        </Box>
        <Box display="flex" style={{ gap: '8px' }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              if (id) {
                loadWorkOrderData(id);
              }
            }}
            disabled={loadingData}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/workorders')}
          >
            Back to List
          </Button>
        </Box>
      </Box>

      {/* Error Display */}
      {(error || apiError) && (
        <Alert severity="error">
          {error || apiError}
        </Alert>
      )}

      {/* Loading Display */}
      {loadingData && (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      )}

      {/* Work Order Details */}
      <Paper style={{ padding: '1rem', marginBottom: '1rem' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle2" color="textSecondary">Quantity</Typography>
            <Typography variant="body1">{selectedWorkOrder.qty}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle2" color="textSecondary">Status</Typography>
            <Chip
              label={selectedWorkOrder.status?.name || 'Unknown'}
              color={getStatusColor(selectedWorkOrder.status?.name || '')}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle2" color="textSecondary">Created</Typography>
            <Typography variant="body2">{formatDateTime(selectedWorkOrder.created_date || '')}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle2" color="textSecondary">Rework</Typography>
            <Chip
              label={selectedWorkOrder.is_rework ? 'Yes' : 'No'}
              color={selectedWorkOrder.is_rework ? 'secondary' : 'default'}
              size="small"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Main Content Tabs */}
      <Paper>
        <Tabs value={currentTab} onChange={handleTabChange} indicatorColor="primary" textColor="primary">
          <Tab icon={<BuildIcon />} label="Operations" />
          <Tab icon={<AssignmentIcon />} label="Work Instructions" />
          <Tab icon={<InputIcon />} label="Inputs" />
          <Tab icon={<ViewIcon />} label="Tool Fixtures" />
          <Tab icon={<SummaryIcon />} label="Summary" />
        </Tabs>

        {/* Operations Tab */}
        <CustomTabPanel value={currentTab} index={0}>
          <Typography variant="h6" gutterBottom>
            Manufacturing Operations
          </Typography>
          
          {operations.length === 0 ? (
            <Alert severity="info">
              No operations defined for this route card.
            </Alert>
          ) : (
            operations.map((operation) => (
              <Card key={operation.id} style={{ marginBottom: '1rem' }}>
                <CardHeader
                  avatar={
                    <Avatar style={{ backgroundColor: '#1976d2' }}>
                      {operation.Sequence}
                    </Avatar>
                  }
                  title={operation.Operation?.Name || 'Unknown Operation'}
                  subheader={`Est. Time: ${operation.EstimatedTime} min | Type: ${operation.Operation?.OperationType?.Name || 'N/A'}`}
                  action={
                    <IconButton
                      onClick={() => handleOperationExpand(operation.id!)}
                      aria-expanded={expandedOperations.includes(operation.id!)}
                    >
                      {expandedOperations.includes(operation.id!) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  }
                />
                <Collapse in={expandedOperations.includes(operation.id!)} timeout="auto" unmountOnExit>
                  <CardContent>
                    <Typography variant="body2" paragraph>
                      {operation.Operation?.Description || 'No description available.'}
                    </Typography>
                    
                    {/* Serial Selection for Operation */}
                    <Box mb={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Select Serial Number</InputLabel>
                        <Select
                          value={selectedSerial}
                          onChange={(e) => setSelectedSerial(e.target.value as string)}
                        >
                          {selectedSerials.map((serial) => (
                            <MenuItem key={serial.id} value={serial.SerialNum}>
                              {serial.SerialNum} - {serial.PartNumber}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Operation Controls */}
                    <Box display="flex" style={{ gap: '8px' }}>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<StartIcon />}
                        onClick={() => handleStartOperation(operation, selectedSerial)}
                        disabled={!selectedSerial}
                        size="small"
                      >
                        Start
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<PauseIcon />}
                        size="small"
                      >
                        Pause
                      </Button>
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<PassIcon />}
                        size="small"
                      >
                        Pass
                      </Button>
                      <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<FailIcon />}
                        size="small"
                      >
                        Fail
                      </Button>
                    </Box>

                    {/* Operation Logs for this Operation */}
                    <Box mt={2}>
                      <Typography variant="subtitle2" gutterBottom>
                        Recent Activity
                      </Typography>
                      {operationLogsLoading ? (
                        <LinearProgress />
                      ) : (
                        <List dense>
                          {activeOperationLogs
                            .filter(log => log.OperationKey === operation.id)
                            .slice(0, 3)
                            .map((log) => (
                              <ListItem key={log.id}>
                                <ListItemText
                                  primary={`${log.SerialNumber} - ${log.Status}`}
                                  secondary={`${formatDateTime(log.StartTime)} | ${calculateDuration(log.StartTime, log.EndTime)}`}
                                />
                                <ListItemSecondaryAction>
                                  <Chip
                                    icon={getStatusIcon(log.Status)}
                                    label={log.Status}
                                    color={getStatusColor(log.Status)}
                                    size="small"
                                  />
                                </ListItemSecondaryAction>
                              </ListItem>
                            ))}
                        </List>
                      )}
                    </Box>
                  </CardContent>
                </Collapse>
              </Card>
            ))
          )}
        </CustomTabPanel>

        {/* Work Instructions Tab */}
        <CustomTabPanel value={currentTab} index={1}>
          <Typography variant="h6" gutterBottom>
            Work Instructions
          </Typography>
          <Alert severity="info">
            Work instructions will be loaded here based on the selected operation.
          </Alert>
        </CustomTabPanel>

        {/* Inputs Tab */}
        <CustomTabPanel value={currentTab} index={2}>
          <Typography variant="h6" gutterBottom>
            Operation Inputs
          </Typography>
          <Alert severity="info">
            Input requirements will be shown here for each operation.
          </Alert>
        </CustomTabPanel>

        {/* Tool Fixtures Tab */}
        <CustomTabPanel value={currentTab} index={3}>
          <Typography variant="h6" gutterBottom>
            Tools & Fixtures
          </Typography>
          <Alert severity="info">
            Required tools and fixtures will be listed here.
          </Alert>
        </CustomTabPanel>

        {/* Summary Tab */}
        <CustomTabPanel value={currentTab} index={4}>
          <Typography variant="h6" gutterBottom>
            Work Order Summary
          </Typography>
          
          {/* Serials Management */}
          <Card style={{ marginBottom: '1rem' }}>
            <CardHeader title="Serial Numbers" />
            <CardContent>
              {serialsLoading ? (
                <LinearProgress />
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Serial Number</TableCell>
                        <TableCell>Part Number</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedSerials.map((serial) => (
                        <TableRow key={serial.id}>
                          <TableCell>{serial.SerialNum}</TableCell>
                          <TableCell>{serial.PartNumber}</TableCell>
                          <TableCell>{serial.PartDescription}</TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              color="primary"
                              onClick={() => handleApproveSerial(serial.SerialNum)}
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              color="secondary"
                              onClick={() => handleRejectSerial(serial.SerialNum)}
                            >
                              Reject
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* Operation Logs */}
          <Card>
            <CardHeader title="Operation History" />
            <CardContent>
              {operationLogsLoading ? (
                <LinearProgress />
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Serial</TableCell>
                        <TableCell>Operation</TableCell>
                        <TableCell>Operator</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Start Time</TableCell>
                        <TableCell>Duration</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {activeOperationLogs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell>{log.SerialNumber}</TableCell>
                          <TableCell>{log.Operation?.Name || 'N/A'}</TableCell>
                          <TableCell>{log.Operator?.Name || 'N/A'}</TableCell>
                          <TableCell>
                            <Chip
                              icon={getStatusIcon(log.Status)}
                              label={log.Status}
                              color={getStatusColor(log.Status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{formatDateTime(log.StartTime)}</TableCell>
                          <TableCell>{calculateDuration(log.StartTime, log.EndTime)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </CustomTabPanel>
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default WorkOrderEdit; 